import React, { useRef, useState } from "react";
import { http, formatApiError, API } from "@/lib/api";
import { Camera, X } from "@phosphor-icons/react";

const MAX_BYTES = 6 * 1024 * 1024;

// Client-side compression (resize so max edge = 1600px, JPEG q=0.85)
function compressImage(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            const maxEdge = 1600;
            let { width, height } = img;
            if (width > maxEdge || height > maxEdge) {
                if (width > height) { height = Math.round(height * maxEdge / width); width = maxEdge; }
                else { width = Math.round(width * maxEdge / height); height = maxEdge; }
            }
            const canvas = document.createElement("canvas");
            canvas.width = width; canvas.height = height;
            canvas.getContext("2d").drawImage(img, 0, 0, width, height);
            canvas.toBlob(
                (blob) => blob ? resolve(new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" })) : reject(new Error("Compression failed")),
                "image/jpeg", 0.85
            );
        };
        img.onerror = reject;
        img.src = url;
    });
}

export default function PhotoUploader({ value = [], onChange, maxPhotos = 5 }) {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const token = typeof window !== "undefined" ? localStorage.getItem("br_token") : null;

    async function handleFiles(files) {
        setError("");
        const remaining = maxPhotos - value.length;
        const list = Array.from(files).slice(0, remaining);
        if (list.length === 0) return;
        setUploading(true);
        const newPaths = [];
        for (const raw of list) {
            try {
                if (raw.size > MAX_BYTES) throw new Error(`${raw.name} is larger than 6 MB`);
                const compressed = await compressImage(raw);
                const fd = new FormData();
                fd.append("file", compressed);
                const { data } = await http.post("/uploads/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
                newPaths.push(data.storage_path);
            } catch (e) { setError(formatApiError(e) || e.message); }
        }
        setUploading(false);
        if (newPaths.length) onChange([...value, ...newPaths]);
    }

    function removeAt(idx) {
        const next = value.slice(); next.splice(idx, 1); onChange(next);
    }

    function thumbUrl(path) {
        // path is like "butler-room/uploads/<uid>/<uuid>.jpg"
        const t = token ? `?auth=${encodeURIComponent(token)}` : "";
        return `${API}/files/${path}${t}`;
    }

    return (
        <div data-testid="photo-uploader">
            <input
                ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden
                onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
            />
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {value.map((p, i) => (
                    <div key={p + i} data-testid={`photo-thumb-${i}`} className="relative aspect-square bg-slate-950 border border-white/10 rounded-sm overflow-hidden group">
                        <img src={thumbUrl(p)} alt="" className="w-full h-full object-cover" />
                        <button
                            type="button"
                            data-testid={`photo-remove-${i}`}
                            onClick={() => removeAt(i)}
                            className="absolute top-1 right-1 bg-slate-950/80 hover:bg-red-500/80 text-white p-1 rounded-sm opacity-0 group-hover:opacity-100 transition"
                        >
                            <X size={14} weight="bold" />
                        </button>
                    </div>
                ))}
                {value.length < maxPhotos && (
                    <button
                        type="button"
                        data-testid="photo-add-btn"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        className="aspect-square bg-slate-950 border border-dashed border-white/15 rounded-sm flex flex-col items-center justify-center text-slate-400 hover:border-gold hover:text-gold transition disabled:opacity-50"
                    >
                        <Camera size={24} weight="duotone" />
                        <span className="text-xs mt-2">{uploading ? "Uploading…" : "Add photo"}</span>
                    </button>
                )}
            </div>
            {error && <p data-testid="photo-error" className="text-xs text-red-400 mt-2">{error}</p>}
            <p className="text-xs text-slate-500 mt-3">
                Add up to {maxPhotos} photos. Real project photos build trust.
            </p>
        </div>
    );
}
